// components
import React from 'react';
import { MenuItem } from '@mui/material';

import GridPageAbstract from '../abstract/GridPageAbstract';
import { mergeHeadWithStorage } from '../../utils/gridHeadStorage';
import CreateTransactionDialog from '../dialogs/CreateTransactionDialog';
import PayoutReceiptsDialog from '../dialogs/PayoutReceiptsDialog';
import Iconify from '../../components/iconify';
import i18n from '../../i18n';
import { isTerminalState } from '../../utils/instanceStates';
import { INSTANCE_STATE } from '../../constants/instanceState';
import { hasReadPermission } from '../../utils/menuPermissions';
import { readFileAsBase64 } from '../../utils/fileBase64';

// Must match action-permissions.yml and co.itfed.spring.api.utils.ActionPermissions.
// Confirm and reject are separate permissions: confirming asserts money moved,
// rejecting only fails something already stuck.
const CONFIRM_ACTION = 'manual-payout-confirm';
const REJECT_ACTION = 'manual-payout-reject';
// The receipts' controller in permissions.yml.
const RECEIPTS_CONTROLLER = 'payout-documents';
// What core takes as a receipt, and its cap.
const RECEIPT_TYPES = 'image/png,image/jpeg,application/pdf';
const RECEIPT_MAX_BYTES = 5 * 1024 * 1024;

export default class PayoutInstancesPage extends GridPageAbstract {
    constructor(props) {
        super(props);
        this.Name = 'payoutInstances';
        this.Server = this.props.context.getState().server.payoutInstances;
        this.MenuDisplayType = 'menu';
        // Created only from a merchant terminal's Create payment / Create payout
        // action, which goes through core's signed merchant API and therefore picks
        // a gateway, reserves a wallet and writes the event trail. The generic add
        // would INSERT a row directly — a transaction no gateway has heard of — so
        // the button is gone and permissions.yml denies create to every role,
        // admin included. Edit and delete too: an instance changes only through
        // the manual decision actions in its menu and, once decided, an appeal.
        this.disableAdd = true;
        this.disableGlobalEditor = true;
        this.disableGlobalDelete = true;
        this.searchFields = ['uuid', 'gatewayTransactionId', 'merchantTransactionId'];

        const defaultHead = [
            { id: 'uuid', label: 'uuid', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            { id: 'orderUuid', format: 'uuid', group: 'identification', label: 'orderUuid', alignRight: false, cell: 'Text', displayGrid: true },
            {
                id: 'merchantTerminalId', required: true, group: 'routing',
                label: 'merchantTerminal',
                alignRight: false,
                cell: 'Select',
                from: 'publicMerchantTerminals',
                object: 'merchantTerminal',
                show: 'name',
                displayGrid: true
            },
            {
                id: 'gatewayId', required: true, group: 'routing',
                label: 'gateway',
                alignRight: false,
                cell: 'Select',
                from: 'publicGateways',
                object: 'gateway',
                show: 'name',
                displayGrid: true
            },
            // The gateway settings it was created with: its actions follow these,
            // not what the gateway links now. Shown from the row, so no list is
            // loaded for them. Gateway users never get them (permissions.config.js).
            {
                id: 'payoutSettingsId', group: 'routing',
                label: 'payoutSettings',
                alignRight: false,
                cell: 'Select',
                object: 'payoutSettings',
                show: 'name',
                displayGrid: true,
                disableEditor: true
            },
            {
                id: 'payoutCallbackSettingsId', group: 'routing',
                label: 'payoutCallbackSettings',
                alignRight: false,
                cell: 'Select',
                object: 'payoutCallbackSettings',
                show: 'name',
                displayGrid: true,
                disableEditor: true
            },
            {
                id: 'walletId', group: 'routing',
                label: 'wallet',
                alignRight: false,
                cell: 'Select',
                from: 'publicWallets',
                object: 'wallet',
                show: 'uuid',
                displayGrid: true
            },
            { id: 'requestedAmount', required: true, group: 'amounts', label: 'requestedAmount', alignRight: true, cell: 'Amount', displayGrid: true },
            { id: 'actualAmount', group: 'amounts', label: 'amount', alignRight: true, cell: 'Amount', displayGrid: true },
            { id: 'walletAmount', group: 'amounts', label: 'gatewayAmount', alignRight: true, cell: 'Amount', displayGrid: true },
            { id: 'conversionRate', group: 'amounts', label: 'conversionRate', alignRight: true, cell: 'Number', displayGrid: true },
            // Fixed by core when the instance is created: the gateway's balance
            // currency and the rate into it. The API ignores all three on a request.
            {
                id: 'balanceCurrencyId', group: 'amounts',
                label: 'balanceCurrency',
                alignRight: false,
                cell: 'Select',
                from: 'publicCurrencies',
                object: 'balanceCurrency',
                show: 'code',
                displayGrid: true,
                disableEditor: true
            },
            { id: 'balanceRate', group: 'amounts', label: 'balanceRate', alignRight: true, cell: 'Number', displayGrid: true, disableEditor: true },
            { id: 'balanceAmount', group: 'amounts', label: 'balanceAmount', alignRight: true, cell: 'Amount', currencyObject: 'balanceCurrency', displayGrid: true, disableEditor: true },
            {
                id: 'state',
                label: 'state',
                alignRight: false,
                cell: 'State',
                items: INSTANCE_STATE,
                displayGrid: true,
                disableEditor: true
            },
            { id: 'stateDesc', label: 'stateDesc', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            { id: 'stateMessage', label: 'statusDesc', alignRight: false, cell: 'BigText', displayGrid: true, disableEditor: true },
            { id: 'internalStateDescription', label: 'internalStateDescription', alignRight: false, cell: 'BigText', humanizeDurations: true, displayGrid: true, disableEditor: true },
            // The appeal that last changed its outcome, set by core-cron when it
            // applies one. Gateway users never get it (permissions.config.js).
            { id: 'appealId', label: 'changedByAppeal', alignRight: true, cell: 'Number', prefix: '#', blankWhenNull: true, displayGrid: true, disableEditor: true },
            { id: 'payload', label: 'payload', alignRight: false, cell: 'Json', displayGrid: false, disableEditor: true },
            { id: 'callbackUrl', label: 'callbackUrl', alignRight: false, cell: 'Url', displayGrid: true, disableEditor: true },
            { id: 'receiverInfo', label: 'receiverInfo', alignRight: false, cell: 'Json', displayGrid: true, disableEditor: true },
            { id: 'gatewayTransactionId', label: 'gatewayTransactionId', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            { id: 'merchantTransactionId', label: 'merchantTransaction', alignRight: false, cell: 'Text', displayGrid: true, disableEditor: true },
            { id: 'totalIncludedCommission', label: 'totalIncludedCommission', alignRight: true, cell: 'Amount', displayGrid: true, disableEditor: true },
            { id: 'totalOntopCommission', label: 'totalOntopCommission', alignRight: true, cell: 'Amount', displayGrid: true, disableEditor: true },
            { id: 'totalAgentCommission', label: 'totalAgentCommission', alignRight: true, cell: 'Amount', displayGrid: true, disableEditor: true },
            { id: 'commissionAgentJson', label: 'commissionAgentJson', alignRight: false, cell: 'Json', displayGrid: false, disableEditor: true },
            { id: 'commissionMerchantJson', label: 'commissionMerchantJson', alignRight: false, cell: 'Json', displayGrid: false, disableEditor: true },
            { id: 'commissionGwJson', label: 'commissionGwJson', alignRight: false, cell: 'Json', displayGrid: false, disableEditor: true },
            { id: 'isLocked', label: 'isLocked', alignRight: false, cell: 'Boolean', displayGrid: true, disableEditor: true },
            { id: 'isFinalized', label: 'isFinalized', alignRight: false, cell: 'Boolean', displayGrid: true, disableEditor: true },
            {
                id: 'dateCreatedModified',
                elements: ['dateCreated', 'dateModified'],
                label: 'dateCreatedModified',
                alignRight: false,
                cell: 'CreateModify',
                disableEditor: true,
                displayGrid: true,
                customSort: 'dateCreated'
            }
        ];
        this.Head = mergeHeadWithStorage(this.Name, defaultHead);

        this.state = {
            ...this.state,
            head: this.Head,
            decisionAction: null,
            decisionId: null,
            receiptsPayout: null
        };
    }

    openDecision(action) {
        const id = this.state.menuClickItemId;
        this.handleCloseMenu();
        this.setState({ decisionAction: action, decisionId: id });
    }

    closeDecision = () => {
        this.setState({ decisionAction: null, decisionId: null });
        // The row's state changed, so the grid behind the dialog is now stale.
        this.retrieveData();
    };

    openReceipts = () => {
        const id = this.state.menuClickItemId;
        const row = this.List.find((item) => item.id === id);
        this.handleCloseMenu();
        this.setState({ receiptsPayout: row ? { id: row.id, uuid: row.uuid } : null });
    };

    closeReceipts = () => {
        this.setState({ receiptsPayout: null });
    };

    /**
     * Confirm, with the receipt read into the request when one was chosen. Only
     * confirm takes one: a receipt says the money went, which a reject denies.
     */
    confirmWithReceipt = async (id, { text, receipt, walletId }) => {
        const file = receipt instanceof Blob ? receipt : null;
        const sent = file ? { contentType: file.type, content: await readFileAsBase64(file) } : undefined;
        // The card only for a payout that had none to pick from: see renderDecision.
        return walletId != null
            ? this.Server.confirm(id, text, sent, walletId)
            : this.Server.confirm(id, text, sent);
    };

    renderCustomMenuItems(id) {
        const row = this.List.find((item) => item.id === id);
        // Nothing to decide on a transaction that already finished — the API
        // refuses it, and offering the action would only produce an error.
        const decidable = !isTerminalState(row?.state);

        const items = [];
        if (decidable && this.allowedAction(CONFIRM_ACTION)) {
            items.push(
                <MenuItem key={`${id}-confirm`} onClick={() => this.openDecision('confirm')}>
                    <Iconify icon="eva:checkmark-circle-2-outline" sx={{ mr: 2 }} />
                    {i18n.t('buttons.manualConfirm')}
                </MenuItem>
            );
        }
        if (decidable && this.allowedAction(REJECT_ACTION)) {
            items.push(
                <MenuItem key={`${id}-reject`} onClick={() => this.openDecision('reject')}>
                    <Iconify icon="eva:close-circle-outline" sx={{ mr: 2 }} />
                    {i18n.t('buttons.manualReject')}
                </MenuItem>
            );
        }
        // On every state: a receipt comes with the decision or after it, so a
        // finished payout is where one is looked for. Only when the controller is
        // listed for the role — any 403 signs the user out.
        if (hasReadPermission(this.state.permissions, RECEIPTS_CONTROLLER)) {
            items.push(
                <MenuItem key={`${id}-receipts`} onClick={this.openReceipts}>
                    <Iconify icon="eva:attach-2-outline" sx={{ mr: 2 }} />
                    {i18n.t('dialogs.payoutReceipts')}
                </MenuItem>
            );
        }
        return items;
    }

    renderCustom() {
        const { receiptsPayout } = this.state;
        return (
            <>
                {this.renderDecision()}
                {receiptsPayout && (
                    <PayoutReceiptsDialog
                        openDialog
                        payout={receiptsPayout}
                        closeDialog={this.closeReceipts}
                        server={this.props.context.getState().server.payoutDocuments}
                        files={this.props.context.getState().server.files}
                    />
                )}
            </>
        );
    }

    renderDecision() {
        const { decisionAction, decisionId } = this.state;
        if (!decisionAction || !decisionId) return null;

        const isConfirm = decisionAction === 'confirm';
        // The decision itself is the endpoint, so all the operator supplies is
        // the evidence for it: a reference or a reason and, for a confirm, the
        // bank's receipt if they have it.
        const head = [
            {
                id: 'text',
                label: isConfirm ? 'reference' : 'reason',
                cell: 'BigText',
                required: true,
                helperText: isConfirm ? 'manualPayoutReference' : 'manualRejectReason'
            }
        ];
        const row = this.List.find((item) => item.id === decisionId);
        if (isConfirm && row && !row.wallet) {
            // Its gateway names the card at confirmation: no card was chosen when it
            // was created, so the operator says which one the money left from — one
            // of the payout's gateway's, which core checks again.
            head.push({
                id: 'walletId',
                label: 'wallet',
                cell: 'ServerSearch',
                required: true,
                searchController: 'publicWallets',
                // None: a uuid matches only whole, so the gateway's live cards are
                // fetched by the prefilter alone and narrowed here as the operator types.
                searchFields: [],
                show: 'uuid',
                limit: 100,
                sort: [{ field: 'id', direction: 'ASC' }],
                prefilter: [
                    { field: 'gateway.id', op: '=', value: row.gateway?.id },
                    { field: 'isArchived', op: '=', value: false }
                ],
                helperText: 'payoutWalletAtConfirmation'
            });
        }
        if (isConfirm) {
            head.push({
                id: 'receipt',
                label: 'receipt',
                cell: 'File',
                accept: RECEIPT_TYPES,
                maxBytes: RECEIPT_MAX_BYTES,
                helperText: 'payoutReceipt'
            });
        }

        return (
            <CreateTransactionDialog
                dialogOpen
                type={`payout-${decisionAction}`}
                id={decisionId}
                Head={head}
                dialogTitle={isConfirm ? 'dialogs.manualConfirm' : 'dialogs.manualReject'}
                saveLabel={isConfirm ? 'buttons.manualConfirm' : 'buttons.manualReject'}
                // Never prefill a reference from a previous decision: it would
                // attach one payout's bank reference to another.
                enablePrevData={false}
                server={(id, body) =>
                    isConfirm ? this.confirmWithReceipt(id, body) : this.Server.reject(id, body.text)
                }
                onClose={this.closeDecision}
            />
        );
    }
}
